import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Calendar, Home, DollarSign, Building, HelpCircle, Users, Palette, BookOpen, Shield } from 'lucide-react';

const ctaButtons = [
  {
    icon: Calendar,
    text: "Book & Schedule",
    onClick: () => window.open('https://calendly.com/udaadu', '_blank')
  },
  {
    icon: Building,
    text: "What Fits My Lot?",
    onClick: () => window.open('/landing', '_self')
  },
  {
    icon: DollarSign,
    text: "Get Price & ROI Report",
    onClick: () => window.open('/adu-calculator', '_self')
  },
  {
    icon: Home,
    text: "Our Portfolio",
    onClick: () => window.open('/our-work', '_self')
  },
  {
    icon: HelpCircle,
    text: "Our FAQ",
    onClick: () => window.open('/faq', '_self')
  }
];

const HowToUseADUAsMiniDaycareOrLearningPod = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-mini-daycare-or-learning-pod" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Safe, Private, Play-Based Learning
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            If you're an early childhood educator or parent forming a micro-school, a backyard ADU offers the ideal setup for a small licensed daycare or learning pod.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Core Setup Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>Core Setup Elements</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Open floor space for activity</span>
                </div>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Cubby storage and shelf-height learning tools</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Soft flooring, washable surfaces</span>
                </div>
                <div className="flex items-center gap-3">
                  <Palette className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Mini snack station or low sink</span>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <img 
                src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&h=400" 
                alt="Mini Daycare Learning Pod ADU" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Licensing & ROI Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Licensing & ROI Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Business Advantages</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Convert ADU for licensed childcare</li>
                <li>• Claim operating expenses</li>
                <li>• Increase property's educational appeal</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Safety Features</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Indoor-outdoor access to gated yard</li>
                <li>• Child-safe materials and fixtures</li>
                <li>• Proper ventilation and lighting</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Age-Specific Design Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>Designed for Ages 2–6</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Learning Zones</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Literacy zone with rug and beanbags</li>
                <li>• Art corner with small easels</li>
                <li>• Circle-time mat and toddler table</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Development Features</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Sensory play stations</li>
                <li>• Motor skill development areas</li>
                <li>• Quiet nap and rest spaces</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>Why Choose an ADU for Childcare?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Shield className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Safe Environment</h3>
              <p className="text-muted-foreground">Controlled, secure setting designed specifically for children</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Users className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Small Groups</h3>
              <p className="text-muted-foreground">Intimate learning environment with personalized attention</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <DollarSign className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Income Potential</h3>
              <p className="text-muted-foreground">Licensed daycare provides steady revenue stream</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Create a Nurturing Learning Environment
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Build a safe, licensed childcare space that serves your community and generates income.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl w-full h-[600px] p-0">
          <div className="w-full h-full">
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUAsMiniDaycareOrLearningPod;